/**
 * Typed API client (FE-01 §6.5). The only hand-written code in this package; `generated/schema.ts`
 * and `generated/error-codes.ts` come from packages/shared/contracts by `just gen-client` (SH-02).
 */
import createClient, { type Client, type Middleware } from "openapi-fetch";

import type { ErrorCode } from "./generated/error-codes";
import type { paths } from "./generated/schema";

export { ERROR_HTTP_STATUS, type ErrorCode } from "./generated/error-codes";
export type { components, operations, paths } from "./generated/schema";

/** A catalog code, or a newer code this build does not know yet (SH-02 FR-SH02-13). */
export type ApiErrorCode = ErrorCode | (string & {});

/** Fallback when the response carried no envelope; the UI shows `errors.unknown` (FE-03). */
export const UNKNOWN_ERROR_CODE = "unknown";

export interface ErrorEnvelope {
  ok: false;
  error: { code: string; message: string; params: Record<string, unknown>; request_id?: string };
}

/** An error response from the API, converted from the `{ok:false, error}` envelope. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode;
  readonly params: Record<string, unknown>;
  readonly requestId: string | null;

  constructor(status: number, body: ErrorEnvelope["error"], requestId: string | null) {
    super(body.message);
    this.name = "ApiError";
    this.status = status;
    this.code = body.code;
    this.params = body.params ?? {};
    this.requestId = body.request_id || requestId;
  }
}

/** The request never got an answer (offline, DNS, timeout). */
export class NetworkError extends Error {
  readonly code: "network.offline" | "network.timeout";

  constructor(code: "network.offline" | "network.timeout", cause?: unknown) {
    super(code, { cause });
    this.name = "NetworkError";
    this.code = code;
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

/** Stable error code for translation (`t('errors.<code>')`, FE-03). */
export function errorCode(e: unknown): ApiErrorCode {
  if (e instanceof ApiError || e instanceof NetworkError) return e.code;
  return UNKNOWN_ERROR_CODE;
}

export interface ApiClientOptions {
  baseUrl: string;
  /** Current UI language, sent as Accept-Language ("es-CR" | "en"). */
  getLanguage: () => string;
  /** Called once per 401 on non-auth endpoints (FE-01 FR-FE01-20). */
  onUnauthenticated?: () => void;
  fetch?: typeof globalThis.fetch;
  /** Default 20 s (FR-FE01-19). */
  timeoutMs?: number;
  /** Uploads (multipart, binary bodies) get 120 s (FR-FE01-19). */
  uploadTimeoutMs?: number;
  /**
   * Reachability seen by the client (FE-04 FR-FE04-28): `"offline"` on a failed request
   * (`network.offline`), `"online"` whenever a response arrives.
   */
  onNetworkStatus?: (status: "online" | "offline") => void;
}

export type ApiClient = Client<paths>;

const DEFAULT_TIMEOUT_MS = 20_000;
const UPLOAD_TIMEOUT_MS = 120_000;
const UPLOAD_TYPES =
  /^(multipart\/form-data|application\/octet-stream|application\/pdf|image\/|video\/|audio\/)/i;

/** A request that sends a file (multipart or a binary body) gets the upload timeout. */
export function isUploadRequest(input: RequestInfo | URL, init?: RequestInit): boolean {
  const body = init?.body;
  if (
    (typeof FormData !== "undefined" && body instanceof FormData) ||
    (typeof Blob !== "undefined" && body instanceof Blob) ||
    body instanceof ArrayBuffer
  ) {
    return true;
  }
  const headers = new Headers(
    init?.headers ??
      (typeof Request !== "undefined" && input instanceof Request ? input.headers : undefined),
  );
  return UPLOAD_TYPES.test(headers.get("Content-Type") ?? "");
}

function isEnvelope(value: unknown): value is ErrorEnvelope {
  return (
    typeof value === "object" &&
    value !== null &&
    (value as { ok?: unknown }).ok === false &&
    typeof (value as { error?: { code?: unknown } }).error?.code === "string"
  );
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const uploadTimeoutMs = options.uploadTimeoutMs ?? UPLOAD_TIMEOUT_MS;
  const baseFetch = options.fetch ?? globalThis.fetch.bind(globalThis);

  const fetchWithTimeout: typeof globalThis.fetch = async (input, init) => {
    const controller = new AbortController();
    const limit = isUploadRequest(input, init) ? uploadTimeoutMs : timeoutMs;
    const timer = setTimeout(() => controller.abort(), limit);
    try {
      const response = await baseFetch(input, {
        ...init,
        signal: init?.signal ?? controller.signal,
      });
      options.onNetworkStatus?.("online");
      return response;
    } catch (cause) {
      const timedOut = controller.signal.aborted;
      if (!timedOut) options.onNetworkStatus?.("offline");
      throw new NetworkError(timedOut ? "network.timeout" : "network.offline", cause);
    } finally {
      clearTimeout(timer);
    }
  };

  const client = createClient<paths>({
    baseUrl: options.baseUrl,
    credentials: "include",
    fetch: fetchWithTimeout,
  });

  const middleware: Middleware = {
    onRequest({ request }) {
      request.headers.set("X-SOS", "1");
      request.headers.set("Accept-Language", options.getLanguage());
      return request;
    },
    async onResponse({ request, response }) {
      if (response.ok) return response;
      const requestId = response.headers.get("X-Request-Id");
      const body: unknown = await response
        .clone()
        .json()
        .catch(() => null);
      if (response.status === 401 && !new URL(request.url).pathname.startsWith("/v1/auth/")) {
        options.onUnauthenticated?.();
      }
      const error = isEnvelope(body)
        ? body.error
        : { code: UNKNOWN_ERROR_CODE, message: response.statusText || "", params: {} };
      throw new ApiError(response.status, error, requestId);
    },
  };
  client.use(middleware);
  return client;
}
